import { useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import {
    api,
    AssignmentEstado,
    AssignmentTargetType,
    LinkedInAssignment,
} from '../../../services/api'
import { Input, Select, TextArea, Button, Label, FormGroup } from '../../../components/shared/StyledComponents'
import { Alert, ESTADO_LABELS } from './linkedinStyles'

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const slideIn = keyframes`
  from { transform: translateY(-20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Above the detail modal (1000): assignments are now started from inside it,
     so this one always opens on top of another overlay. */
  z-index: 1100;
  animation: ${fadeIn} 0.2s ease-out;
`

const Modal = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  width: 95%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  animation: ${slideIn} 0.3s ease-out;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg} ${({ theme }) => theme.borderRadius.lg} 0 0;
`

const ModalTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.lg};
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  line-height: 1;
  opacity: 0.8;
  &:hover { opacity: 1; }
`

const ModalBody = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`

const TargetBox = styled.div`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
`

const ESTADO_OPTIONS: AssignmentEstado[] = ['asignada', 'en_progreso', 'completada', 'cancelada']

interface InternUser {
    id: number
    username: string
    display_name: string
    user_type?: string
}

export interface AssignTarget {
    target_type: AssignmentTargetType
    target_id: number
    target_title: string
}

interface AssignTaskModalProps {
    isOpen: boolean
    onClose: () => void
    /** Preset target when launching from a Repo/Paper card or table row. */
    target?: AssignTarget
    /** When editing an existing assignment. */
    assignment?: LinkedInAssignment
    onSaved: () => void
}

const todayStr = () => new Date().toISOString().split('T')[0]

export const AssignTaskModal: React.FC<AssignTaskModalProps> = ({
    isOpen,
    onClose,
    target,
    assignment,
    onSaved,
}) => {
    const isEdit = !!assignment
    const [interns, setInterns] = useState<InternUser[]>([])
    const [userId, setUserId] = useState<number | ''>('')
    const [fechaInicio, setFechaInicio] = useState(todayStr())
    const [fechaFin, setFechaFin] = useState('')
    const [capacity, setCapacity] = useState(100)
    const [progress, setProgress] = useState(0)
    const [internalRepoUrl, setInternalRepoUrl] = useState('')
    const [estado, setEstado] = useState<AssignmentEstado>('asignada')
    const [notes, setNotes] = useState('')
    const [saving, setSaving] = useState(false)
    const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

    // Effective target: either the explicit prop, or derived from the edited assignment.
    const effectiveTarget: AssignTarget | undefined = target ?? (assignment
        ? {
            target_type: assignment.target_type,
            target_id: assignment.target_id,
            target_title: assignment.target_title || `${assignment.target_type} #${assignment.target_id}`,
        }
        : undefined)

    // Load interns (user_type === 'intern', excluding 'admin').
    useEffect(() => {
        if (!isOpen) return
        let cancelled = false
        api.users.getAll()
            .then((res) => {
                if (cancelled) return
                const data = Array.isArray(res.data) ? res.data : []
                setInterns(
                    data.filter((u: any) =>
                        u.user_type === 'intern' && (u.username || '').toLowerCase() !== 'admin'
                    )
                )
            })
            .catch(() => {
                if (!cancelled) setInterns([])
            })
        return () => { cancelled = true }
    }, [isOpen])

    // Initialize form when opening / switching between create and edit.
    useEffect(() => {
        if (!isOpen) return
        setAlert(null)
        if (assignment) {
            setUserId(assignment.user_id)
            setFechaInicio(assignment.fecha_inicio || todayStr())
            setFechaFin(assignment.fecha_fin || '')
            setCapacity(assignment.capacity_pct ?? 100)
            setProgress(assignment.progress_pct ?? 0)
            setInternalRepoUrl(assignment.internal_repo_url ?? '')
            setEstado((assignment.estado as AssignmentEstado) || 'asignada')
            setNotes(assignment.notes || '')
        } else {
            // A fresh assignment must not inherit the previous one's state.
            setInternalRepoUrl('')
            setProgress(0)
            setUserId('')
            setFechaInicio(todayStr())
            setFechaFin('')
            setCapacity(100)
            setEstado('asignada')
            setNotes('')
        }
    }, [isOpen, assignment])

    if (!isOpen) return null

    const handleSubmit = async () => {
        setAlert(null)

        if (!userId) {
            setAlert({ type: 'error', message: 'Selecciona un interno.' })
            return
        }
        if (!effectiveTarget) {
            setAlert({ type: 'error', message: 'No se ha especificado el objetivo de la asignación.' })
            return
        }
        if (!fechaInicio) {
            setAlert({ type: 'error', message: 'La fecha de inicio es obligatoria.' })
            return
        }
        // Validate fecha_fin >= fecha_inicio when fecha_fin is set.
        if (fechaFin && fechaFin < fechaInicio) {
            setAlert({ type: 'error', message: 'La fecha de fin debe ser igual o posterior a la de inicio.' })
            return
        }

        setSaving(true)
        try {
            if (isEdit && assignment) {
                // The assignee and target are fixed once created (the backend rejects
                // them on update), so only the editable fields are sent.
                await api.linkedin.updateAssignment(assignment.id, {
                    fecha_inicio: fechaInicio,
                    fecha_fin: fechaFin || null,
                    capacity_pct: capacity,
                    progress_pct: progress,
                    estado,
                    notes: notes || null,
                    internal_repo_url: internalRepoUrl.trim() || null,
                })
            } else {
                await api.linkedin.createAssignment({
                    user_id: Number(userId),
                    target_type: effectiveTarget.target_type,
                    target_id: effectiveTarget.target_id,
                    fecha_inicio: fechaInicio,
                    fecha_fin: fechaFin || null,
                    capacity_pct: capacity,
                    estado,
                    notes: notes || null,
                    internal_repo_url: internalRepoUrl.trim() || null,
                })
            }
            setAlert({ type: 'success', message: 'Asignación guardada correctamente.' })
            onSaved()
            // Brief pause so the success message is visible before closing.
            setTimeout(() => { onClose() }, 600)
        } catch (err: any) {
            const status = err?.response?.status
            const detail = err?.response?.data?.detail
            const message = status === 409
                ? 'Este interno ya tiene una asignación activa para este objetivo.'
                : (detail || 'Error al guardar la asignación.')
            setAlert({ type: 'error', message })
        } finally {
            setSaving(false)
        }
    }

    const targetLabel = { paper: 'Paper', project: 'Proyecto' }[effectiveTarget?.target_type as string] ?? 'Repositorio'

    return (
        <Overlay onClick={onClose}>
            <Modal onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <ModalTitle>{isEdit ? '✏️ Editar asignación' : '🎓 Asignar a interno'}</ModalTitle>
                    <CloseButton onClick={onClose}>&times;</CloseButton>
                </ModalHeader>

                <ModalBody>
                    {effectiveTarget && (
                        <TargetBox>
                            <strong>{targetLabel}:</strong> {effectiveTarget.target_title}
                        </TargetBox>
                    )}

                    {alert && <Alert $type={alert.type}>{alert.message}</Alert>}

                    <FormGroup>
                        <Label>Interno</Label>
                        {/* Reassigning is not an edit: close this task and assign a new one. */}
                        <Select
                            value={userId}
                            disabled={isEdit}
                            title={isEdit ? 'Para cambiar de interno, cierra esta tarea y asígnala de nuevo.' : undefined}
                            onChange={(e) => setUserId(e.target.value ? Number(e.target.value) : '')}
                        >
                            <option value="">Selecciona un interno…</option>
                            {interns.map((u) => (
                                <option key={u.id} value={u.id}>{u.display_name || u.username}</option>
                            ))}
                        </Select>
                    </FormGroup>

                    <FormGroup style={{ display: 'flex', gap: '1rem' }}>
                        <div style={{ flex: 1 }}>
                            <Label>Fecha de inicio</Label>
                            <Input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <Label>Fecha de fin</Label>
                            <Input type="date" value={fechaFin} min={fechaInicio} onChange={(e) => setFechaFin(e.target.value)} />
                        </div>
                    </FormGroup>

                    <FormGroup style={{ display: 'flex', gap: '1rem' }}>
                        <div style={{ flex: 1 }}>
                            <Label>Dedicación (%)</Label>
                            <Input
                                type="number"
                                min="0"
                                max="100"
                                value={capacity}
                                onChange={(e) => setCapacity(parseInt(e.target.value, 10) || 0)}
                            />
                        </div>
                        {isEdit && (
                            <div style={{ flex: 1 }}>
                                <Label>Progreso (%)</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={progress}
                                    onChange={(e) => setProgress(parseInt(e.target.value, 10) || 0)}
                                />
                            </div>
                        )}
                        <div style={{ flex: 1 }}>
                            <Label>Estado</Label>
                            <Select value={estado} onChange={(e) => setEstado(e.target.value as AssignmentEstado)}>
                                {ESTADO_OPTIONS.map((s) => (
                                    <option key={s} value={s}>{ESTADO_LABELS[s]}</option>
                                ))}
                            </Select>
                        </div>
                    </FormGroup>

                    <FormGroup>
                        <Label>Repo de trabajo (GitLab interno)</Label>
                        <Input
                            type="url"
                            value={internalRepoUrl}
                            onChange={(e) => setInternalRepoUrl(e.target.value)}
                            placeholder="https://gitlab.../mi-investigacion"
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label>Notas</Label>
                        <TextArea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Notas opcionales sobre la tarea…"
                        />
                    </FormGroup>

                    <Footer>
                        <Button variant="secondary" onClick={onClose} disabled={saving}>
                            Cancelar
                        </Button>
                        <Button variant="primary" onClick={handleSubmit} disabled={saving}>
                            {saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Asignar'}
                        </Button>
                    </Footer>
                </ModalBody>
            </Modal>
        </Overlay>
    )
}

export default AssignTaskModal
